// client/src/components/IssueTriage/FloatingFeedbackButton.tsx
import { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquarePlus, X } from 'lucide-react';

interface FloatingFeedbackButtonProps {
  /** Callback when the feedback button is clicked */
  onClick: () => void;
  /** Show onboarding hint for first-time users */
  showHint?: boolean;
  /** Callback when the hint tooltip is dismissed */
  onDismissHint?: () => void;
  /**
   * 'saved' (default): the user's dragged position, else the bottom-right corner.
   * 'midRight': docked at the middle of the right edge and not draggable, for pages whose own
   * actions live in the bottom-right corner (the issue-triage detail panel's action row and its
   * upward Move menu); the position the user saved for other pages is left untouched.
   */
  placement?: 'saved' | 'midRight';
}

const HINT_DISMISSED_KEY = 'officehub_feedback_hint_dismissed';
const BUTTON_STORAGE_KEY = 'oh_feedback_button_pos';
const DRAG_THRESHOLD = 5;

function getDefaultPos() {
  return { bottom: 24, right: 24 };
}

function loadPos(key: string, fallback: { bottom: number; right: number }) {
  try {
    const saved = localStorage.getItem(key);
    return saved ? JSON.parse(saved) : fallback;
  } catch {
    return fallback;
  }
}

function getPointerXY(e: MouseEvent | TouchEvent | React.MouseEvent | React.TouchEvent) {
  if ('touches' in e && e.touches.length > 0) {
    return { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }
  if ('clientX' in e) {
    return { x: e.clientX, y: e.clientY };
  }
  return { x: 0, y: 0 };
}

/**
 * FloatingFeedbackButton — independently draggable feedback button.
 *
 * Drag to reposition (mouse + touch). Click to open feedback form.
 * Position saved to localStorage.
 */
export function FloatingFeedbackButton({
  onClick,
  showHint: externalShowHint,
  onDismissHint,
  placement = 'saved',
}: FloatingFeedbackButtonProps) {
  const docked = placement === 'midRight';
  const [showLocalHint, setShowLocalHint] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [pos, setPos] = useState(() => loadPos(BUTTON_STORAGE_KEY, getDefaultPos()));

  const isDragging = useRef(false);
  const hasMoved = useRef(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const posStart = useRef({ bottom: 0, right: 24 });

  // First-time hint
  useEffect(() => {
    const dismissed = localStorage.getItem(HINT_DISMISSED_KEY);
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (!dismissed && externalShowHint !== false) {
      timer = setTimeout(() => setShowLocalHint(true), 2000);
    }
    return () => { if (timer) clearTimeout(timer); };
  }, [externalShowHint]);

  // Drag handlers (mouse + touch)
  const handlePointerDown = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      const { x, y } = getPointerXY(e);
      isDragging.current = true;
      hasMoved.current = false;
      dragStart.current = { x, y };
      posStart.current = { ...pos };
    },
    [pos]
  );

  useEffect(() => {
    const onMove = (e: MouseEvent | TouchEvent) => {
      if (!isDragging.current) return;
      const { x, y } = getPointerXY(e);
      const dx = x - dragStart.current.x;
      const dy = y - dragStart.current.y;
      if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
        hasMoved.current = true;
        if ('touches' in e) e.preventDefault();
      }
      if (hasMoved.current) {
        setPos({
          right: Math.max(0, posStart.current.right - dx),
          bottom: Math.max(0, posStart.current.bottom - dy),
        });
      }
    };
    const onUp = () => {
      if (isDragging.current) {
        isDragging.current = false;
        if (hasMoved.current) {
          setPos((p: { bottom: number; right: number }) => {
            localStorage.setItem(BUTTON_STORAGE_KEY, JSON.stringify(p));
            return p;
          });
        }
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, []);

  const showHint = externalShowHint ?? showLocalHint;

  const handleDismissHint = () => {
    setShowLocalHint(false);
    localStorage.setItem(HINT_DISMISSED_KEY, 'true');
    onDismissHint?.();
  };

  const handleClick = () => {
    // Docked (triage) mode has no drag handler, so a drag flag left over from another page never applies.
    if (!docked && hasMoved.current) return; // was a drag, not a click
    handleDismissHint();
    onClick();
  };

  return (
    <div
      className="fixed z-[60]"
      data-testid="feedback-button-anchor"
      style={docked ? { top: '50%', right: 24, transform: 'translateY(-50%)' } : { bottom: pos.bottom, right: pos.right }}
    >
      <div className="flex items-end gap-3">
        {/* Onboarding hint tooltip */}
        {showHint && (
          <div className="relative animate-in slide-in-from-right-2 fade-in duration-300">
            <div className="bg-slate-900 text-white text-sm px-4 py-3 rounded-lg shadow-lg max-w-xs">
              <button
                onClick={handleDismissHint}
                className="absolute -top-2 -right-2 bg-slate-700 hover:bg-slate-600 rounded-full p-2 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Dismiss hint"
              >
                <X className="h-4 w-4" />
              </button>
              <p className="font-medium mb-1">Found a bug or have feedback?</p>
              <p className="text-slate-300 text-xs">
                Click this button or press{' '}
                <kbd className="bg-slate-700 px-1.5 py-0.5 rounded text-xs font-mono">
                  Ctrl+Shift+B
                </kbd>
              </p>
            </div>
            <div className="absolute top-1/2 -right-2 transform -translate-y-1/2">
              <div className="border-8 border-transparent border-l-slate-900" />
            </div>
          </div>
        )}

        {/* Floating button */}
        <button
          onMouseDown={docked ? undefined : handlePointerDown}
          onTouchStart={docked ? undefined : handlePointerDown}
          onClick={handleClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className={`group relative bg-amber-500 hover:bg-amber-600 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 ${docked ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'}`}
          aria-label="Submit feedback"
          title="Report a bug or give feedback (Ctrl+Shift+B)"
        >
          <MessageSquarePlus className="h-6 w-6" />

          {isHovered && !showHint && (
            <div className="absolute bottom-full right-0 mb-2 whitespace-nowrap animate-in fade-in duration-150 pointer-events-none">
              <div className="bg-slate-900 text-white text-xs px-3 py-2 rounded-lg shadow-lg">
                <span className="font-medium">Give Feedback</span>
                <span className="text-slate-400 ml-2">Ctrl+Shift+B</span>
              </div>
            </div>
          )}
        </button>
      </div>
    </div>
  );
}
